import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@octano/global-ui';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Card } from 'reactstrap';
import PersonalBackground from './PersonalBackground';
import { PathsLayouts } from '../../../../config/routes';
import EducationalBackground from './EducationalBackground';
import EmploymentBackground from './EmploymentBackground';
import HiringInformation from './HiringInformation';
import AdditionalDocumentation from './AdditionalDocumentation';
import TeacherInfoLoader, {
  useTeacherInfoLoader,
} from './parts/Loaders/TeacherInfoLoader';
import { TranslationsKeys } from '../../../../locales/translations';
import AssignedStudents from './AssignedStudent';

interface TabComponentProps {
  currentTab: string;
  onTabChange: (tabId: string | number) => void;
}

const TabComponent = memo(({ currentTab, onTabChange }: TabComponentProps) => {
  const { t } = useTranslation(TranslationsKeys.TEACHER_MANAGMENT_TEACHER_FILE);
  const { data: teacherInfo, loading } = useTeacherInfoLoader();

  if (
    !loading &&
    !teacherInfo?.isHired &&
    currentTab === 'hiring-information'
  ) {
    currentTab = 'personal-background';
  }

  const tabs = [
    {
      path: 'personal-background',
      name: 'personalBackground',
      component: PersonalBackground,
    },
    {
      path: 'educational-background',
      name: 'educationalBackground',
      component: EducationalBackground,
    },
    {
      path: 'employment-background',
      name: 'employmentBackground',
      component: EmploymentBackground,
    },
  ];

  if (teacherInfo?.isHired) {
    tabs.push({
      path: 'hiring-information',
      name: 'hiringInformation',
      component: HiringInformation,
    });
  }
  if (teacherInfo?.isTutor) {
    tabs.push({
      path: 'assigned-students',
      name: 'assignedStudents',
      component: AssignedStudents,
    });
  }

  tabs.push({
    path: 'additional-documentation',
    name: 'additionalDocumentation',
    component: AdditionalDocumentation,
  });

  return (
    <Card className="mt-5 mx-3">
      <Tabs defaultTab={currentTab} onSelection={onTabChange}>
        <TabsList>
          {tabs.map((tab) => (
            <Tab key={tab.path} id={tab.path}>
              {t(`tabs.${tab.name}`)}
            </Tab>
          ))}
        </TabsList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.path} id={tab.path}>
              <tab.component />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Card>
  );
});

function TeacherInformation() {
  const location = useLocation();
  const history = useHistory();
  const { teacherId } = useParams<{ teacherId: string }>();

  const getTabFromPath = useCallback(() => {
    return location.pathname.split('/').pop() || 'sections';
  }, [location.pathname]);

  const [currentTab, setCurrentTab] = useState(getTabFromPath);

  const handleTabChange = useCallback(
    (tabId: string | number) => {
      if (tabId !== currentTab) {
        const routeToGo = `${PathsLayouts.teacherFile}/${teacherId}/${tabId}`;
        history.replace(routeToGo);
        setCurrentTab(tabId as string);
      }
    },
    [currentTab, history, teacherId],
  );

  useEffect(() => {
    setCurrentTab(getTabFromPath());
  }, [location.pathname, getTabFromPath]);

  return (
    <TeacherInfoLoader
      key={`teacher-info-${teacherId}`}
      teacherId={Number(teacherId)}
    >
      <TabComponent currentTab={currentTab} onTabChange={handleTabChange} />
    </TeacherInfoLoader>
  );
}

export default TeacherInformation;
